import { useState, useCallback, memo } from 'react';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { SectionHeader } from './SectionHeader';
import { SplitPaneEditor } from './SplitPaneEditor';
import { DeleteSectionModal } from './DeleteSectionModal';
import { HistoryModal } from './HistoryModal';
import { MarkdownPreview } from './MarkdownPreview';
import type { DocType, DocumentSection, SectionMetadata, SectionType } from '../../types/document';

interface SectionProps {
  section: DocumentSection;
  metadata?: SectionMetadata;
  docType: DocType;
  /** All available upstream anchors for coverage dropdown (PRD anchors for design sections) */
  upstreamAnchors?: string[];
  /** Title lookup for upstream anchors (anchor -> section title) */
  upstreamTitleMap?: Record<string, string>;
  /** Anchors not covered by any sibling section (shown with warning icon in dropdown) */
  uncoveredAnchors?: Set<string>;
  activeField: 'title' | 'body' | null;
  isDragging: boolean;
  onActivateTitle: () => void;
  onActivateBody: () => void;
  onDeactivate: () => void;
  onSave: (body: string) => void;
  onTitleChange: (newTitle: string) => void;
  /** Called when coverage selection changes (design sections) */
  onCoversChange?: (covers: string[]) => void;
  onDelete: () => void;
  onNavigateNextFromBody?: () => void;
  onShiftReturnFromBody?: () => void;
  onNavigateToTitle?: () => void;
  onNavigateToBody?: () => void;
  onNavigatePrevFromTitle?: () => void;
  dragHandleProps?: SyntheticListenerMap;
  /** Story ID for task navigation display */
  storyId?: string;
  /** Called when user clicks a design capability in PRD coverage indicator */
  onNavigateToDesign?: (anchor: string) => void;
  /** Called when user clicks a task in design coverage indicator */
  onNavigateToTask?: (taskSeq: string) => void;
  /** Called when user clicks a PRD anchor in design covers dropdown */
  onNavigateToPRD?: (anchor: string) => void;
  /** Called when section type changes (Story 250) */
  onSectionTypeChange?: (type: SectionType) => void;
}


export const Section = memo(function Section({
  section,
  metadata,
  docType,
  upstreamAnchors,
  upstreamTitleMap,
  uncoveredAnchors,
  activeField,
  isDragging,
  onActivateTitle,
  onActivateBody,
  onDeactivate,
  onSave,
  onTitleChange,
  onCoversChange,
  onDelete,
  onNavigateNextFromBody,
  onShiftReturnFromBody,
  onNavigateToTitle,
  onNavigateToBody,
  onNavigatePrevFromTitle,
  dragHandleProps,
  storyId,
  onNavigateToDesign,
  onNavigateToTask,
  onNavigateToPRD,
  onSectionTypeChange,
}: SectionProps) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isActive = activeField !== null;
  const isTitleActive = activeField === 'title';
  const isBodyActive = activeField === 'body';

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const handleActivateBody = useCallback(() => {
    onActivateBody();
  }, [onActivateBody]);

  const handleDeactivate = useCallback(() => {
    onDeactivate();
  }, [onDeactivate]);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    setShowDeleteModal(false);
    onDelete();
  }, [onDelete]);

  // Story 232: History modal handlers
  const handleHistoryClick = useCallback(() => {
    setShowHistoryModal(true);
  }, []);

  const handleHistoryRestore = useCallback((restoredBody: string) => {
    // Save the restored body content
    onSave(restoredBody);
    setShowHistoryModal(false);
  }, [onSave]);

  return (
    <>
      <div
        data-testid={`section-${section.section_key}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          border: isActive
            ? '2px solid var(--ceetrix-400, #818cf8)'
            : isDragging
              ? '2px solid var(--ceetrix-300, #a5b4fc)'
              : '1px solid var(--border-default, #e2e8f0)',
          borderRadius: '8px',
          marginBottom: '16px',
          backgroundColor: 'var(--bg-base)',
          boxShadow: isDragging ? '0 8px 20px -4px rgba(0, 0, 0, 0.15)' : 'none',
          borderColor: isActive || isDragging || isHovered
            ? undefined // Use the border colors defined above
            : 'transparent',
        }}
      >
        <SectionHeader
          title={section.title}
          covers={section.covers ?? []}
          upstreamAnchors={upstreamAnchors}
          upstreamTitleMap={upstreamTitleMap}
          uncoveredAnchors={uncoveredAnchors}
          metadata={metadata}
          docType={docType}
          isActive={isActive}
          isTitleEditing={isTitleActive}
          isCollapsed={isCollapsed}
          isSectionHovered={isHovered}
          onToggleCollapse={handleToggleCollapse}
          onDelete={handleDeleteClick}
          onTitleChange={onTitleChange}
          onCoversChange={onCoversChange}
          onActivateTitle={onActivateTitle}
          onDeactivateTitle={handleDeactivate}
          onNavigateUp={onNavigatePrevFromTitle}
          onNavigateDown={onNavigateToBody}
          dragHandleProps={dragHandleProps}
          storyId={storyId}
          onNavigateToDesign={onNavigateToDesign}
          onNavigateToTask={onNavigateToTask}
          onNavigateToPRD={onNavigateToPRD}
          sectionType={section.section_type}
          anchor={section.anchor}
          onSectionTypeChange={onSectionTypeChange}
          onHistory={section.id ? handleHistoryClick : undefined}
          isStale={section.is_stale}
        />

        {/* Content area - clicking here activates edit mode */}
        {!isCollapsed && (
          <div
            onClick={!isBodyActive ? handleActivateBody : undefined}
            style={{
              padding: isBodyActive ? 0 : '16px 24px 24px 24px',
              cursor: isBodyActive ? 'default' : 'pointer',
              minHeight: isBodyActive ? undefined : '40px',
            }}
          >
            {isBodyActive ? (
              <SplitPaneEditor
                initialContent={section.body}
                onChange={onSave}
                onEscape={handleDeactivate}
                onNavigateNext={onNavigateNextFromBody}
                onNavigatePrev={onNavigateToTitle}
                onShiftReturn={onShiftReturnFromBody}
              />
            ) : (
              <MarkdownPreview content={section.body} className="prose" />
            )}
          </div>
        )}
      </div>

      {showDeleteModal && (
        <DeleteSectionModal
          sectionTitle={section.title}
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {/* Story 232: Section history modal */}
      {showHistoryModal && section.id && (
        <HistoryModal
          sectionId={section.id}
          sectionTitle={section.title}
          currentBody={section.body}
          onClose={() => setShowHistoryModal(false)}
          onRestore={handleHistoryRestore}
        />
      )}
    </>
  );
});
